import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from './Card';
import { useRecentToolLogs } from '../hooks/useRecentToolLogs';

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return '—';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(isoString).toLocaleDateString();
}

function StatusBadge({ success }: { success: boolean }) {
  return (
    <span className={`badge ${success ? 'bg-success' : 'bg-danger'}`}>
      {success ? 'Success' : 'Failed'}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[1, 2, 3, 4, 5].map((i) => (
        <td key={i}>
          <div
            style={{
              height: '14px',
              background: '#e9ecef',
              borderRadius: '4px',
              width: `${50 + (i * 10) % 40}%`,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

export function RecentToolLogsTable() {
  const { t } = useTranslation();
  const { logs, isLoading, error } = useRecentToolLogs(10);

  return (
    <Card title={t('dashboard.recent_tool_logs')} collapsible className="h-100">
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>{t('common.time')}</th>
              <th>{t('common.tool')}</th>
              <th>{t('call_logs.duration')}</th>
              <th>{t('common.agent')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={5} className="text-danger">
                  <i className="bi bi-exclamation-triangle mr-1" />
                  {error}
                </td>
              </tr>
            ) : isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted text-center">
                  {t('dashboard.no_recent_tool_logs')}
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.logId}>
                  <td>{formatRelativeTime(log.createdAt)}</td>
                  <td>
                    <code style={{ fontSize: '12px' }}>{log.toolName}</code>
                  </td>
                  <td>{log.durationMs}ms</td>
                  <td>{log.agentName}</td>
                  <td>
                    <StatusBadge success={log.success} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2">
        <Link to="/admin/tool-logs" className="text-sm">
          {t('common.view_all')} → <i className="bi bi-arrow-right" />
        </Link>
      </div>
    </Card>
  );
}
